import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Swal from "sweetalert2";
import {
  createAutomation,
  getAutomationById,
  updateAutomation,
  createAutomationRule,
  updateAutomationRule,
} from "./services/AutomationServices";
import UserContext from "../../contexts/UserContext";
import NotificationsContext from "../../contexts/NotificationsContext";

// Importar contexto do AgentHub (opcional)
import { useAgentHubContext } from "../AgentHub/contexts/AgentHubContext";
import {
  IconArrowLeft,
  IconDeviceFloppy,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import styled from "styled-components";

import AutomationRuleWizard from "./components/AutomationRuleWizard";
import TemplateTypeSelector from "./components/TemplateTypeSelector";
import TemplateSelector from "./components/TemplateSelector";
import "./AutomationRuleForm.css";

// Schema de validação para automação
const automationSchema = yup
  .object({
    name: yup.string().required("Nome é obrigatório"),
    automation_type: yup.string().required("Tipo de automação é obrigatório"),
    followup_ai_prompt: yup.string().when("automation_type", {
      is: "followup",
      then: (schema) => schema.required("Prompt de IA é obrigatório para follow-up automático"),
      otherwise: (schema) => schema.notRequired(),
    }),
  })
  .required();

// Schema de validação para regra
const ruleSchema = yup
  .object({
    template_action: yup.string().required("O tipo de mensagem é obrigatório"),
    time_value: yup
      .number()
      .required("O valor do tempo é obrigatório")
      .min(1, "Valor mínimo é 1"),
    time_unit: yup.string().required("A unidade de tempo é obrigatória"),
    direction: yup.string().required("A direção é obrigatória"),
    event: yup.string().required("O evento é obrigatório"),
    reference: yup.string().required("A referência é obrigatória"),
  })
  .required();

const AutomationWithRulesWizard = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { setNotify } = useContext(NotificationsContext);

  // Detectar contexto do AgentHub
  const agentContext = useAgentHubContext();
  const contextAgentId = agentContext?.agentId || agentId;
  const isInsideAgentHub =
    Boolean(agentContext) || window.location.pathname.includes("/agent-hub/");

  // Estados principais
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [createdAutomationId, setCreatedAutomationId] = useState(null);
  const [automationData, setAutomationData] = useState(null);

  // Estados para templates
  const [availableTemplates, setAvailableTemplates] = useState({
    whatsapp: [],
    email: [],
    webhook: [],
  });
  const [selectedTemplateType, setSelectedTemplateType] = useState("whatsapp");
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Form para automação
  const automationForm = useForm({
    resolver: yupResolver(automationSchema),
    defaultValues: {
      name: "",
      description: "",
      automation_type: "whatsapp",
      active: true,
      followup_ai_prompt:
        "Analise o histórico da conversa e gere insights sobre o cliente.",
    },
  });

  // Form para regra
  const ruleForm = useForm({
    resolver: yupResolver(ruleSchema),
    defaultValues: {
      template_action: "confirmation_message",
      template: "",
      time_value: 1,
      time_unit: "days",
      direction: "before",
      event: "appointment",
      reference: "start",
      priority: 1,
      active: true,
    },
  });

  // Reset template quando tipo muda
  useEffect(() => {
    ruleForm.setValue("template", "");
    setSelectedTemplate(null);
  }, [selectedTemplateType, ruleForm]);

  const handleTemplateTypeSelect = (type) => {
    setSelectedTemplateType(type);
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    ruleForm.setValue("template", template.id.toString());
  };

  // Função para criar automação (etapa 0)
  const handleAutomationSubmit = async (data) => {
    setLoading(true);
    try {
      const automationData = {
        ...data,
        account_type: "generic",
        account_id: user.account_id,
        user: user.user_id,
        ...(contextAgentId && { agent: contextAgentId }),
      };

      const response = await createAutomation(automationData);
      setCreatedAutomationId(response.id);
      setAutomationData(response);

      // Buscar templates disponíveis
      if (response.available_templates) {
        setAvailableTemplates({
          whatsapp: response.available_templates.whatsapp || [],
          email: response.available_templates.email || [],
          webhook: response.available_templates.webhook || [],
        });
      }

      setNotify({
        title: "Sucesso!",
        message: "Automação criada com sucesso! Agora configure as regras.",
        type: "success",
        visible: true,
      });

      // Para follow-up automático, pular seleção de templates e ir direto para timing
      if (response.automation_type === 'followup') {
        setCurrentStep(1); // Pular para configuração de timing (step 1 no wizard filtrado)
      } else {
        // Avançar para próxima etapa automaticamente (seleção de templates)
        setCurrentStep(1);
      }
    } catch (error) {
      console.error("Erro ao salvar automação:", error);

      let errorMessage = "Ocorreu um erro ao salvar a automação.";
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        const errorDetails = Object.entries(errorData)
          .map(
            ([field, messages]) =>
              `${field}: ${
                Array.isArray(messages) ? messages.join(", ") : messages
              }`
          )
          .join("; ");

        if (errorDetails) {
          errorMessage += ` Detalhes: ${errorDetails}`;
        }
      }

      setNotify({
        title: "Erro!",
        message: errorMessage,
        type: "error",
        visible: true,
      });
    } finally {
      setLoading(false);
    }
  };


  // Função para criar regra (para templates e follow-up)
  const handleRuleSubmit = async (data) => {
    const isFollowupType = automationData?.automation_type === 'followup';
    
    // Para templates, validar seleção do template
    if (!isFollowupType && !selectedTemplate) {
      setNotify({
        title: "Atenção!",
        message: "Selecione um template para continuar.",
        type: "warning",
        visible: true,
      });
      return;
    }

    setLoading(true);

    try {
      const formData = {
        ...data,
        user: user.user_id,
        account_id: user.account_id,
      };

      // Para automações com template
      if (!isFollowupType) {
        formData.template_id = selectedTemplate.id;
        formData.template_action = selectedTemplate.action;
        formData.template_type = selectedTemplateType;
      } else {
        // Para follow-up, usar action genérico
        formData.template_action = 'followup';
        formData.template_type = 'followup';
      }

      await createAutomationRule(createdAutomationId, formData);

      Swal.fire({
        title: "Sucesso!",
        text: isFollowupType 
          ? "Automação de follow-up e regra criadas com sucesso!"
          : "Automação e regra criadas com sucesso!",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      // Navegar de volta para lista
      if (isInsideAgentHub) {
        navigate(`/agent-hub/${contextAgentId}/automations`);
      } else {
        navigate("/automations");
      }
    } catch (error) {
      console.error("Erro ao salvar regra:", error);
      Swal.fire({
        title: "Erro!",
        text: "Ocorreu um erro ao salvar a regra.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  // Validações por etapa
  const validateStep0 = async () => {
    try {
      await automationSchema.validate(automationForm.getValues(), {
        abortEarly: false,
      });
      return true;
    } catch (validationError) {
      setNotify({
        title: "Campos obrigatórios",
        message:
          "Por favor, preencha todos os campos obrigatórios antes de continuar.",
        type: "error",
        visible: true,
      });
      return false;
    }
  };

  const validateStep1 = () => {
    return selectedTemplateType !== null;
  };

  const validateStep2 = () => {
    return selectedTemplate !== null;
  };

  const validateStep3 = async () => {
    const currentData = ruleForm.watch();
    try {
      await ruleSchema.validate(currentData, { abortEarly: false });
      return true;
    } catch (validationError) {
      setNotify({
        title: "Campos obrigatórios",
        message:
          "Por favor, preencha todos os campos obrigatórios antes de continuar.",
        type: "error",
        visible: true,
      });
      return false;
    }
  };

  // Função para navegar para próxima etapa
  const handleNext = async () => {
    let canProceed = true;

    if (currentStep === 0) {
      canProceed = await validateStep0();
      if (canProceed) {
        await handleAutomationSubmit(automationForm.getValues());
        return false; // Não avançar automaticamente, handleAutomationSubmit já faz isso
      }
    } else if (currentStep === 1) {
      canProceed = validateStep1();
      if (!canProceed) {
        setNotify({
          title: "Selecione um tipo",
          message: "Selecione o tipo de comunicação para continuar.",
          type: "warning",
          visible: true,
        });
      }
    } else if (currentStep === 2) {
      canProceed = validateStep2();
      if (!canProceed) {
        setNotify({
          title: "Selecione um template",
          message: "Selecione um template para continuar.",
          type: "warning",
          visible: true,
        });
      }
    } else if (currentStep === 1 && automationData?.automation_type === 'followup') {
      // Para follow-up no step 1 (timing), validar campos de timing
      canProceed = await validateStep3();
    } else if (currentStep === 3) {
      // Para automações normais no step 3 (timing)
      canProceed = await validateStep3();
    }

    return canProceed;
  };

  const getFilteredTemplates = () => {
    return availableTemplates[selectedTemplateType] || [];
  };

  const getTimeUnitInPortuguese = (unit) => {
    const translations = {
      minutes: "minutos",
      hours: "horas",
      days: "dias",
      weeks: "semanas",
    };
    return translations[unit] || unit;
  };

  // Função para renderizar o conteúdo de cada etapa
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div>
            <div className="automation-rule-form__step-header">
              <div className="automation-rule-form__step-number">1</div>
              <h3 className="automation-rule-form__step-title">
                Nova Automação
              </h3>
            </div>

            <FormSection>
              <SectionTitle>Informações Básicas</SectionTitle>

              <FormRow>
                <FormGroup>
                  <Label>
                    Nome da Automação <RequiredIndicator>*</RequiredIndicator>
                  </Label>
                  <Input
                    type="text"
                    placeholder="Ex: Automação de Consultas Médicas"
                    {...automationForm.register("name")}
                    $error={!!automationForm.formState.errors.name}
                  />
                  {automationForm.formState.errors.name && (
                    <ErrorText>
                      {automationForm.formState.errors.name.message}
                    </ErrorText>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>
                    Tipo de Automação <RequiredIndicator>*</RequiredIndicator>
                  </Label>
                  <Select
                    {...automationForm.register("automation_type")}
                    $error={!!automationForm.formState.errors.automation_type}
                  >
                    <option value="whatsapp">📱 WhatsApp</option>
                    <option value="email">📧 Email</option>
                    <option value="webhook">🔗 Webhook</option>
                    <option value="followup">🤖 Follow-up Automático</option>
                  </Select>
                  {automationForm.formState.errors.automation_type && (
                    <ErrorText>
                      {automationForm.formState.errors.automation_type.message}
                    </ErrorText>
                  )}
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label>Descrição</Label>
                <TextArea
                  rows={3}
                  placeholder="Descreva o propósito desta automação..."
                  {...automationForm.register("description")}
                />
                <HelpText>
                  Opcional: Uma descrição clara ajuda na organização das
                  automações.
                </HelpText>
              </FormGroup>

              {/* Campo específico para follow-up automático */}
              {automationForm.watch("automation_type") === 'followup' && (
                <FormGroup>
                  <Label>
                    Prompt para IA <RequiredIndicator>*</RequiredIndicator>
                  </Label>
                  <TextArea
                    rows={8}
                    placeholder="Digite o prompt que guiará a IA para gerar mensagens de follow-up..."
                    {...automationForm.register("followup_ai_prompt")}
                    $error={!!automationForm.formState.errors.followup_ai_prompt}
                  />
                  {automationForm.formState.errors.followup_ai_prompt && (
                    <ErrorText>
                      {automationForm.formState.errors.followup_ai_prompt.message}
                    </ErrorText>
                  )}
                  <HelpText>
                    Este prompt guiará a IA para analisar o histórico de atendimento para gerar o follow-up do contato.
                  </HelpText>
                </FormGroup>
              )}

              <StatusContainer>
                <StatusToggle>
                  <ToggleContainer>
                    <ToggleInput
                      type="checkbox"
                      id="active-toggle"
                      {...automationForm.register("active")}
                    />
                    <ToggleSlider
                      htmlFor="active-toggle"
                      active={automationForm.watch("active")}
                    >
                      <ToggleKnob active={automationForm.watch("active")} />
                    </ToggleSlider>
                  </ToggleContainer>
                  <StatusInfo>
                    <StatusLabel>
                      {automationForm.watch("active") ? (
                        <>
                          <IconCheck size={18} />
                          Automação Ativa
                        </>
                      ) : (
                        <>
                          <IconX size={18} />
                          Automação Inativa
                        </>
                      )}
                    </StatusLabel>
                    <StatusDescription>
                      {automationForm.watch("active")
                        ? "As regras desta automação serão executadas automaticamente."
                        : "Nenhuma mensagem será enviada automaticamente."}
                    </StatusDescription>
                  </StatusInfo>
                </StatusToggle>
              </StatusContainer>
            </FormSection>
          </div>
        );


      case 2:
        return (
          <div>
            <div className="automation-rule-form__step-header">
              <div className="automation-rule-form__step-number">3</div>
              <h3 className="automation-rule-form__step-title">
                Escolha o Template
              </h3>
            </div>

            {selectedTemplateType && getFilteredTemplates().length > 0 ? (
              <TemplateSelector
                templateType={selectedTemplateType}
                availableTemplates={getFilteredTemplates()}
                selectedTemplate={selectedTemplate}
                onTemplateSelect={handleTemplateSelect}
                loading={false}
              />
            ) : (
              <div
                style={{ textAlign: "center", padding: "40px", color: "#666" }}
              >
                {!selectedTemplateType
                  ? "Primeiro selecione o tipo de comunicação na etapa anterior."
                  : "Nenhum template disponível para o tipo selecionado."}
              </div>
            )}
          </div>
        );

      case 1:
        // Para followup: timing direto
        if (automationData?.automation_type === 'followup') {
          return (
            <div>
              <div className="automation-rule-form__step-header">
                <div className="automation-rule-form__step-number">2</div>
                <h3 className="automation-rule-form__step-title">
                  Configure o Timing do Follow-up
                </h3>
              </div>
              
              <form className="automation-rule-form__form">
                {/* Event Configuration */}
                <div className="automation-rule-form__form-row">
                  <div className="automation-rule-form__form-group">
                    <label className="automation-rule-form__label">
                      Evento
                    </label>
                    <select
                      className={`automation-rule-form__select ${
                        ruleForm.formState.errors.event
                          ? "automation-rule-form__select--error"
                          : ""
                      }`}
                      {...ruleForm.register("event")}
                    >
                      <option value="appointment">Agendamento</option>
                      <option value="data_collection">Coleta de dados</option>
                    </select>
                    {ruleForm.formState.errors.event && (
                      <span className="automation-rule-form__error-text">
                        {ruleForm.formState.errors.event.message}
                      </span>
                    )}
                  </div>
                </div>

                {/* Timing Configuration */}
                <TimingSection>
                  <TimingHeader>
                    <h4 className="automation-rule-form__timing-title">
                      Configuração de Timing
                    </h4>
                  </TimingHeader>

                  <TimingGrid>
                    <TimingGroup>
                      <Label>Tempo</Label>
                      <Input
                        type="number"
                        min="1"
                        $error={!!ruleForm.formState.errors.time_value}
                        {...ruleForm.register("time_value")}
                      />
                      {ruleForm.formState.errors.time_value && (
                        <ErrorText>
                          {ruleForm.formState.errors.time_value.message}
                        </ErrorText>
                      )}
                    </TimingGroup>

                    <TimingGroup>
                      <Label>Unidade</Label>
                      <Select
                        $error={!!ruleForm.formState.errors.time_unit}
                        {...ruleForm.register("time_unit")}
                      >
                        <option value="minutes">Minutos</option>
                        <option value="hours">Horas</option>
                        <option value="days">Dias</option>
                        <option value="weeks">Semanas</option>
                      </Select>
                      {ruleForm.formState.errors.time_unit && (
                        <ErrorText>
                          {ruleForm.formState.errors.time_unit.message}
                        </ErrorText>
                      )}
                    </TimingGroup>
                    
                    <TimingGroup>
                      <Label>Enviar</Label>
                      <Select
                        $error={!!ruleForm.formState.errors.direction}
                        {...ruleForm.register("direction")}
                      >
                        {ruleForm.watch("event") !== "data_collection" && (
                          <option value="before">Antes</option>
                        )}
                        <option value="after">Depois</option>
                      </Select>
                      {ruleForm.formState.errors.direction && (
                        <ErrorText>
                          {ruleForm.formState.errors.direction.message}
                        </ErrorText>
                      )}
                    </TimingGroup>
                    
                    <TimingGroup>
                      <Label>Referência</Label>
                      <Select
                        $error={!!ruleForm.formState.errors.reference}
                        {...ruleForm.register("reference")}
                      >
                        {ruleForm.watch("event") !== "data_collection" && (
                          <option value="start">Início</option>
                        )}
                        <option value="created_at">Criação</option>
                      </Select>
                      {ruleForm.formState.errors.reference && (
                        <ErrorText>
                          {ruleForm.formState.errors.reference.message}
                        </ErrorText>
                      )}
                    </TimingGroup>
                  </TimingGrid>

                  <TimingExample>
                    <ExampleText>
                      Exemplo: Enviar{" "}
                      {ruleForm.watch("direction") === "before"
                        ? "antes"
                        : "depois"}{" "}
                      do{" "}
                      {ruleForm.watch("event") === "appointment"
                        ? "agendamento"
                        : "evento"}{" "}
                      {ruleForm.watch("time_value")}{" "}
                      {getTimeUnitInPortuguese(ruleForm.watch("time_unit"))}{" "}
                      baseado na{" "}
                      {ruleForm.watch("reference") === "start"
                        ? "data de início"
                        : "data de criação"}
                    </ExampleText>
                  </TimingExample>
                </TimingSection>

                {/* Active Toggle */}
                <div className="automation-rule-form__form-group">
                  <div className="automation-rule-form__checkbox-container">
                    <input
                      type="checkbox"
                      id="rule-active-toggle"
                      className="automation-rule-form__checkbox"
                      {...ruleForm.register("active")}
                    />
                    <label
                      htmlFor="rule-active-toggle"
                      className="automation-rule-form__checkbox-label"
                    >
                      Regra Ativa
                    </label>
                  </div>
                  <span className="automation-rule-form__help-text">
                    Se desativada, esta regra específica não gerará mensagens.
                  </span>
                </div>
              </form>
            </div>
          );
        }
        
        // Para outros tipos: tipo de comunicação
        return (
          <div>
            <div className="automation-rule-form__step-header">
              <div className="automation-rule-form__step-number">2</div>
              <h3 className="automation-rule-form__step-title">
                Selecione o Tipo de Comunicação
              </h3>
            </div>

            <TemplateTypeSelector
              selectedType={selectedTemplateType}
              onTypeSelect={handleTemplateTypeSelect}
              availableTemplates={availableTemplates}
            />
          </div>
        );

      case 3:
        const isFollowupType = automationData?.automation_type === 'followup';
        return (
          <div>
            <div className="automation-rule-form__step-header">
              <div className="automation-rule-form__step-number">4</div>
              <h3 className="automation-rule-form__step-title">
                Configure o Timing
              </h3>
            </div>

            {(selectedTemplate || isFollowupType) ? (
              <form className="automation-rule-form__form">
                {/* Message Type and Event */}
                <div className="automation-rule-form__form-row">
                  {/* {!isFollowupType && (
                    <div className="automation-rule-form__form-group">
                      <label className="automation-rule-form__label">
                        Tipo de Mensagem
                      </label>
                      <select
                        className={`automation-rule-form__select ${
                          ruleForm.formState.errors.template_action
                            ? "automation-rule-form__select--error"
                            : ""
                        }`}
                        {...ruleForm.register("template_action")}
                      >
                        <option value="confirmation_message">Confirmação</option>
                        <option value="reminder_message">Lembrete</option>
                        <option value="feedback">Feedback</option>
                        <option value="tips_message">Dicas</option>
                        <option value="generic">Genérico</option>
                      </select>
                      {ruleForm.formState.errors.template_action && (
                        <span className="automation-rule-form__error-text">
                          {ruleForm.formState.errors.template_action.message}
                        </span>
                      )}
                    </div>
                  )} */}

                  <div className="automation-rule-form__form-group">
                    <label className="automation-rule-form__label">
                      Evento
                    </label>
                    <select
                      className={`automation-rule-form__select ${
                        ruleForm.formState.errors.event
                          ? "automation-rule-form__select--error"
                          : ""
                      }`}
                      {...ruleForm.register("event")}
                    >
                      <option value="appointment">Agendamento</option>
                      <option value="data_collection">Coleta de dados</option>
                    </select>
                    {ruleForm.formState.errors.event && (
                      <span className="automation-rule-form__error-text">
                        {ruleForm.formState.errors.event.message}
                      </span>
                    )}
                  </div>
                </div>

                {/* Timing Configuration */}
                <TimingSection>
                  <TimingHeader>
                    <h4 className="automation-rule-form__timing-title">
                      Configuração de Timing
                    </h4>
                  </TimingHeader>

                  <TimingGrid>
                    <TimingGroup>
                      <Label>Tempo</Label>
                      <Input
                        type="number"
                        min="1"
                        $error={!!ruleForm.formState.errors.time_value}
                        {...ruleForm.register("time_value")}
                      />
                      {ruleForm.formState.errors.time_value && (
                        <ErrorText>
                          {ruleForm.formState.errors.time_value.message}
                        </ErrorText>
                      )}
                    </TimingGroup>

                    <TimingGroup>
                      <Label>Unidade</Label>
                      <Select
                        $error={!!ruleForm.formState.errors.time_unit}
                        {...ruleForm.register("time_unit")}
                      >
                        <option value="minutes">Minutos</option>
                        <option value="hours">Horas</option>
                        <option value="days">Dias</option>
                        <option value="weeks">Semanas</option>
                      </Select>
                      {ruleForm.formState.errors.time_unit && (
                        <ErrorText>
                          {ruleForm.formState.errors.time_unit.message}
                        </ErrorText>
                      )}
                    </TimingGroup>
                    
                    <TimingGroup>
                      <Label>Enviar</Label>
                      <Select
                        $error={!!ruleForm.formState.errors.direction}
                        {...ruleForm.register("direction")}
                      >
                        {ruleForm.watch("event") !== "data_collection" && (
                          <option value="before">Antes</option>
                        )}
                        <option value="after">Depois</option>
                      </Select>
                      {ruleForm.formState.errors.direction && (
                        <ErrorText>
                          {ruleForm.formState.errors.direction.message}
                        </ErrorText>
                      )}
                    </TimingGroup>
                    
                    <TimingGroup>
                      <Label>Referência</Label>
                      <Select
                        $error={!!ruleForm.formState.errors.reference}
                        {...ruleForm.register("reference")}
                      >
                        {ruleForm.watch("event") !== "data_collection" && (
                          <option value="start">Início</option>
                        )}
                        <option value="created_at">Criação</option>
                      </Select>
                      {ruleForm.formState.errors.reference && (
                        <ErrorText>
                          {ruleForm.formState.errors.reference.message}
                        </ErrorText>
                      )}
                    </TimingGroup>
                  </TimingGrid>

                  <TimingExample>
                    <ExampleText>
                      Exemplo: Enviar{" "}
                      {ruleForm.watch("direction") === "before"
                        ? "antes"
                        : "depois"}{" "}
                      do{" "}
                      {ruleForm.watch("event") === "appointment"
                        ? "agendamento"
                        : "evento"}{" "}
                      {ruleForm.watch("time_value")}{" "}
                      {getTimeUnitInPortuguese(ruleForm.watch("time_unit"))}{" "}
                      baseado na{" "}
                      {ruleForm.watch("reference") === "start"
                        ? "data de início"
                        : "data de criação"}
                    </ExampleText>
                  </TimingExample>
                </TimingSection>

                {/* Active Toggle */}
                <div className="automation-rule-form__form-group">
                  <div className="automation-rule-form__checkbox-container">
                    <input
                      type="checkbox"
                      id="rule-active-toggle"
                      className="automation-rule-form__checkbox"
                      {...ruleForm.register("active")}
                    />
                    <label
                      htmlFor="rule-active-toggle"
                      className="automation-rule-form__checkbox-label"
                    >
                      Regra Ativa
                    </label>
                  </div>
                  <span className="automation-rule-form__help-text">
                    Se desativada, esta regra específica não gerará mensagens.
                  </span>
                </div>
              </form>
            ) : (
              <div
                style={{ textAlign: "center", padding: "40px", color: "#666" }}
              >
                {isFollowupType 
                  ? "Configuração de timing para follow-up automático."
                  : "Primeiro selecione um template na etapa anterior para configurar o timing."
                }
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <MobileContainer>
      {/* Header com botão de voltar */}
      <MobileHeader>
        <BackButton
          onClick={() => {
            if (isInsideAgentHub) {
              navigate(`/agent-hub/${contextAgentId}/automations`);
            } else {
              navigate("/automations");
            }
          }}
        >
          <IconArrowLeft size={20} />
        </BackButton>
      </MobileHeader>

      {/* Wizard Container */}
      <WizardWrapper>
        <AutomationRuleWizard
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          onNext={handleNext}
          onSubmit={() => handleRuleSubmit(ruleForm.getValues())}
          loading={loading}
          isEditing={false}
          includeAutomationStep={true}
          automationType={automationData?.automation_type}
        >
          {renderStepContent()}
        </AutomationRuleWizard>
      </WizardWrapper>
    </MobileContainer>
  );
};

// Styled Components (usando os mesmos do AutomationForm)
const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SectionTitle = styled.h3`
  color: var(--text-primary);
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media (min-width: 576px) {
    gap: 20px;
  }

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const RequiredIndicator = styled.span`
  color: var(--danger);
  font-size: 16px;
`;

const Input = styled.input`
  padding: 14px 16px;
  border: 2px solid
    ${(props) => (props.$error ? "var(--danger)" : "var(--border-color)")};
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  background: white;
  color: var(--text-primary);
  min-height: 44px;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.1);
  }

  &::placeholder {
    color: var(--text-muted);
  }

  @media (max-width: 768px) {
    font-size: 16px; /* Prevent zoom on iOS */
    padding: 16px;
  }
`;

const TextArea = styled.textarea`
  padding: 12px 16px;
  border: 2px solid ${(props) => (props.$error ? "var(--danger)" : "var(--border-color)")};
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  background: white;
  color: var(--text-primary);
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }

  &::placeholder {
    color: var(--text-muted);
  }

  @media (max-width: 768px) {
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

const Select = styled.select`
  padding: 14px 16px;
  border: 2px solid
    ${(props) => (props.$error ? "var(--danger)" : "var(--border-color)")};
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  background: white;
  color: var(--text-primary);
  cursor: pointer;
  min-height: 44px;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.1);
  }

  @media (max-width: 768px) {
    font-size: 16px; /* Prevent zoom on iOS */
    padding: 16px;
  }
`;

const StatusContainer = styled.div`
  background: var(--surface);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid var(--border-color);
`;

const StatusToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ToggleContainer = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const ToggleInput = styled.input`
  opacity: 0;
  position: absolute;
`;

const ToggleSlider = styled.label`
  display: block;
  width: 52px;
  height: 28px;
  background: ${(props) =>
    props.active ? "var(--primary)" : "var(--border-color)"};
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
`;

const ToggleKnob = styled.div`
  width: 22px;
  height: 22px;
  background: white;
  border-radius: 50%;
  position: absolute;
  top: 3px;
  left: ${(props) => (props.active ? "27px" : "3px")};
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const StatusInfo = styled.div`
  flex: 1;
`;

const StatusLabel = styled.h4`
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const StatusDescription = styled.p`
  color: var(--text-secondary);
  font-size: 14px;
  margin: 0;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const ErrorText = styled.span`
  color: var(--danger);
  font-size: 12px;
  font-weight: 500;
`;

const HelpText = styled.span`
  color: var(--text-muted);
  font-size: 12px;
  line-height: 1.3;
`;

// Novos styled components para timing section
const TimingSection = styled.div`
  background: var(--surface);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid var(--border-color);
  
  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 8px;
  }
`;

const TimingHeader = styled.div`
  margin-bottom: 20px;
  
  h4 {
    margin: 0;
    color: var(--text-primary);
    font-size: 16px;
    font-weight: 600;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
  }
`;

const TimingGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  
  @media (min-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
  
  @media (min-width: 992px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const TimingGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TimingExample = styled.div`
  margin-top: 20px;
  padding: 16px;
  background: rgba(var(--primary-rgb), 0.05);
  border-radius: 8px;
  border-left: 4px solid var(--primary);
  
  @media (max-width: 768px) {
    margin-top: 16px;
    padding: 12px;
  }
`;

const ExampleText = styled.span`
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.4;
  font-style: italic;
  
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

// Componentes de layout mobile
const MobileContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #f8f9fa;
  
  @media (max-width: 768px) {
    padding: 0;
  }
`;

const MobileHeader = styled.div`
  padding: 16px;
  background: white;
  border-bottom: 1px solid var(--border-color);
  position: sticky;
  top: 0;
  z-index: 100;
  
  @media (min-width: 769px) {
    position: static;
    padding: 20px;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 8px;
  background: var(--surface);
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--primary);
    color: white;
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const WizardWrapper = styled.div`
  padding: 16px;
  
  @media (min-width: 769px) {
    padding: 0;
  }
  
  .automation-rule-form__step-header {
    margin-bottom: 24px;
    
    @media (max-width: 768px) {
      margin-bottom: 20px;
    }
    
    .automation-rule-form__step-title {
      font-size: 24px;
      font-weight: 600;
      color: var(--text-primary);
      
      @media (max-width: 768px) {
        font-size: 20px;
      }
    }
  }
`;

export default AutomationWithRulesWizard;
